import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Link2, Copy, Eye, Trash2, FileText, CheckCircle2, Clock, AlertTriangle, MessageCircle, ExternalLink } from "lucide-react";

const STATUS_META = {
  pending: { label: "Aguardando", icon: Clock, cls: "bg-amber-500/15 text-amber-700 border-amber-500/30" },
  submitted: { label: "Respondido", icon: CheckCircle2, cls: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30" },
  expired: { label: "Expirado", icon: AlertTriangle, cls: "bg-muted text-muted-foreground border-border" },
};

function CreateModal({ onClose, onCreated, presetPatient = null }) {
  const [form, setForm] = useState({
    patient_name: presetPatient?.name || "",
    patient_email: presetPatient?.email || "",
    patient_id: presetPatient?.id || "",
    expires_in_days: 14,
  });
  const [patients, setPatients] = useState([]);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (presetPatient) return;
    (async () => {
      try {
        const { data } = await api.get("/admin/patients");
        setPatients(data || []);
      } catch (_) {/* noop */}
    })();
  }, [presetPatient]);

  const filteredPatients = query.trim()
    ? patients.filter((p) => (p.name + " " + (p.email || "")).toLowerCase().includes(query.toLowerCase())).slice(0, 6)
    : [];

  const pickPatient = (p) => {
    setForm({ ...form, patient_name: p.name, patient_email: p.email || "", patient_id: p.id });
    setQuery("");
  };

  const publicLink = (token) => `${window.location.origin}/teste/${token}`;

  const submit = async () => {
    if (!form.patient_name.trim()) { toast.error("Informe o nome do paciente."); return; }
    setSaving(true);
    try {
      const payload = {
        patient_name: form.patient_name.trim(),
        patient_email: form.patient_email.trim() || null,
        patient_id: form.patient_id || null,
        expires_in_days: Number(form.expires_in_days) || 14,
      };
      const { data } = await api.post("/admin/metabolic-tests", payload);
      setResult(data);
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao criar teste."); }
    setSaving(false);
  };

  const copyLink = async () => {
    if (!result) return;
    const link = publicLink(result.token);
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link copiado!");
    } catch (_) { toast.error("Não foi possível copiar. Copie manualmente."); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={onClose} data-testid="create-metabolic-modal">
      <div className="bg-card border border-border rounded-sm p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="overline text-gold mb-1">Novo teste</div>
            <h3 className="brand-wordmark uppercase text-lg font-bold text-primary">Gerar link do teste</h3>
            <p className="text-xs text-muted-foreground mt-1">Envie o link ao paciente para que ele responda antes da consulta.</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-sm"><X className="w-4 h-4" /></button>
        </div>

        {!result ? (
          <div className="space-y-3">
            {presetPatient && (
              <div className="text-xs bg-primary/10 border border-primary/30 rounded-sm px-3 py-2 text-primary" data-testid="preset-patient-banner">
                Vinculado a <b>{presetPatient.name}</b> ({presetPatient.email})
              </div>
            )}
            {!presetPatient && (
              <div className="relative">
                <label className="overline text-muted-foreground mb-1 block">Buscar paciente cadastrado (opcional)</label>
                <input
                  value={query} onChange={(e) => setQuery(e.target.value)}
                  className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                  placeholder="Digite nome ou email para vincular à ficha…"
                  data-testid="mt-patient-search"
                />
                {filteredPatients.length > 0 && (
                  <div className="absolute z-10 left-0 right-0 mt-1 bg-card border border-border rounded-sm shadow-lg max-h-52 overflow-y-auto">
                    {filteredPatients.map((p) => (
                      <button key={p.id} type="button" onClick={() => pickPatient(p)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-muted border-b border-border last:border-0"
                        data-testid={`mt-patient-option-${p.id}`}>
                        <div className="font-medium">{p.name}</div>
                        {p.email && <div className="text-xs text-muted-foreground">{p.email}</div>}
                      </button>
                    ))}
                  </div>
                )}
                {form.patient_id && (
                  <div className="mt-1 text-xs text-emerald-700 inline-flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Vinculado à ficha #{form.patient_id.slice(0, 8)}
                    <button type="button" onClick={() => setForm({ ...form, patient_id: "" })} className="ml-1 text-muted-foreground hover:underline">remover</button>
                  </div>
                )}
              </div>
            )}
            <div>
              <label className="overline text-muted-foreground mb-1 block">Nome do paciente *</label>
              <input value={form.patient_name} onChange={(e) => setForm({ ...form, patient_name: e.target.value })}
                className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm" data-testid="mt-patient-name" />
            </div>
            <div>
              <label className="overline text-muted-foreground mb-1 block">Email (opcional)</label>
              <input type="email" value={form.patient_email} onChange={(e) => setForm({ ...form, patient_email: e.target.value })}
                className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm" data-testid="mt-patient-email" />
            </div>
            <div>
              <label className="overline text-muted-foreground mb-1 block">Válido por (dias)</label>
              <input type="number" min="1" max="90" value={form.expires_in_days} onChange={(e) => setForm({ ...form, expires_in_days: e.target.value })}
                className="w-32 px-3 py-2 rounded-sm border border-input bg-card text-sm" />
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <button onClick={onClose} className="px-4 py-2 rounded-sm border border-border text-sm hover:bg-muted">Cancelar</button>
              <button onClick={submit} disabled={saving}
                className="px-4 py-2 rounded-sm bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-60"
                data-testid="mt-create-btn">
                {saving ? "Gerando…" : "Gerar link"}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-sm p-3 text-sm">
              <div className="font-semibold text-emerald-700 mb-1 inline-flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> Link gerado com sucesso
              </div>
              <div className="text-xs text-emerald-700">Copie e envie ao paciente pelo canal de sua preferência.</div>
            </div>
            <div>
              <label className="overline text-muted-foreground mb-1 block">Link compartilhável</label>
              <div className="flex gap-2">
                <input readOnly value={publicLink(result.token)}
                  className="flex-1 px-3 py-2 rounded-sm border border-input bg-muted text-xs font-mono"
                  data-testid="mt-shareable-link"
                  onClick={(e) => e.target.select()} />
                <button onClick={copyLink}
                  className="px-3 py-2 rounded-sm bg-primary text-primary-foreground text-xs font-semibold inline-flex items-center gap-1 hover:bg-primary/90"
                  data-testid="mt-copy-btn">
                  <Copy className="w-3.5 h-3.5" /> Copiar
                </button>
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <button onClick={onClose} className="px-4 py-2 rounded-sm bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90">Concluir</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ViewAnswersModal({ test, onClose }) {
  const [detail, setDetail] = useState(null);
  const [compare, setCompare] = useState(null); // {current, previous, questionnaire, current_score, previous_score}
  const [showCompare, setShowCompare] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [d, c] = await Promise.all([
          api.get(`/admin/metabolic-tests/${test.id}`),
          api.get(`/admin/metabolic-tests/${test.id}/compare`),
        ]);
        setDetail(d.data);
        setCompare(c.data);
        // Fire-and-forget mark-seen so it disappears from dashboard alerts.
        api.post(`/admin/metabolic-tests/${test.id}/mark-seen`).catch(() => {});
      } catch (_) { toast.error("Erro ao carregar respostas."); }
    })();
  }, [test.id]);

  const ans = detail?.answers || {};
  const score = detail?.score;
  const prevAns = compare?.previous?.answers || null;
  const prevScore = compare?.previous_score || null;
  const hasPrevious = !!(compare && compare.previous);

  const changed = (v, pv) => {
    if (pv === undefined || pv === null || pv === "") return false;
    if (Array.isArray(v) && Array.isArray(pv)) return JSON.stringify(v) !== JSON.stringify(pv);
    return String(v) !== String(pv);
  };

  const scoreBucketCls = (v) => {
    if (v === null || v === undefined) return "bg-muted text-muted-foreground border-border";
    if (v >= 80) return "bg-emerald-500/15 text-emerald-700 border-emerald-500/40";
    if (v >= 60) return "bg-amber-500/15 text-amber-700 border-amber-500/40";
    return "bg-rose-500/15 text-rose-700 border-rose-500/40";
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={onClose} data-testid="view-answers-modal">
      <div className="bg-card border border-border rounded-sm p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
          <div>
            <div className="overline text-gold mb-1">Respostas</div>
            <h3 className="brand-wordmark uppercase text-lg font-bold text-primary">{test.patient_name || "Paciente sem nome"}</h3>
            {test.submitted_at && (
              <p className="text-xs text-muted-foreground mt-1">Enviado em {new Date(test.submitted_at).toLocaleString("pt-BR")}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {hasPrevious && (
              <button
                onClick={() => setShowCompare((s) => !s)}
                className={`px-3 py-1.5 rounded-sm border text-xs font-semibold transition-colors ${
                  showCompare ? "bg-primary text-primary-foreground border-primary" : "border-primary text-primary hover:bg-primary/5"
                }`}
                data-testid="compare-toggle-btn"
              >
                {showCompare ? "Ocultar comparação" : "Comparar com anterior"}
              </button>
            )}
            <button onClick={onClose} className="p-1 hover:bg-muted rounded-sm"><X className="w-4 h-4" /></button>
          </div>
        </div>
        {showCompare && compare?.previous && (
          <div className="text-xs bg-primary/10 border border-primary/30 rounded-sm px-3 py-2 mb-4" data-testid="compare-banner">
            Comparando com teste anterior enviado em {new Date(compare.previous.submitted_at).toLocaleString("pt-BR")}. Respostas que mudaram estão destacadas.
          </div>
        )}
        {score && score.global !== null && score.global !== undefined && (
          <div className={`rounded-sm border p-3 mb-4 flex items-center gap-3 flex-wrap ${scoreBucketCls(score.global)}`} data-testid="score-global-badge">
            <div>
              <div className="overline opacity-80">Score global</div>
              <div className="font-heading text-2xl font-bold">
                {score.global}<span className="opacity-70 text-sm font-normal">/100</span>
              </div>
            </div>
            {showCompare && prevScore && prevScore.global !== null && (
              <div className="text-xs opacity-80">
                <div>Anterior: <b>{prevScore.global}/100</b></div>
                <div>Delta: <b className={score.global >= prevScore.global ? "text-emerald-700" : "text-rose-700"}>
                  {score.global >= prevScore.global ? "+" : ""}{score.global - prevScore.global}
                </b></div>
              </div>
            )}
          </div>
        )}
        {!detail ? (
          <div className="py-8 text-center text-sm text-muted-foreground overline">Carregando…</div>
        ) : !detail.answers ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Este teste ainda não foi respondido.</div>
        ) : (
          <div className="space-y-5">
            {(detail.questionnaire || []).map((section) => {
              const sectionAns = section.questions.filter((q) => ans[q.id] !== undefined && ans[q.id] !== "");
              if (sectionAns.length === 0) return null;
              const sScore = score?.by_section?.[section.id];
              return (
                <div key={section.id} className="bg-muted/30 border border-border rounded-sm p-3">
                  <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                    <div className="overline text-gold">{section.title}</div>
                    {sScore !== null && sScore !== undefined && (
                      <span className={`px-2 py-0.5 rounded-sm border text-[10px] font-semibold ${scoreBucketCls(sScore)}`} data-testid={`score-section-${section.id}`}>
                        {sScore}/100
                      </span>
                    )}
                  </div>
                  <dl className="space-y-1.5 text-sm">
                    {sectionAns.map((q) => {
                      const cv = ans[q.id];
                      const pv = prevAns?.[q.id];
                      const diff = showCompare && changed(cv, pv);
                      return (
                        <div key={q.id} className={`flex flex-wrap gap-2 py-1 ${diff ? "bg-amber-500/10 -mx-1 px-1 rounded-sm border-l-2 border-amber-500" : ""}`} data-testid={`ans-row-${q.id}`}>
                          <dt className="text-muted-foreground min-w-[40%]">{q.label}</dt>
                          <dd className="font-medium text-foreground flex-1 min-w-0">
                            <div>
                              {Array.isArray(cv) ? cv.join(", ") : String(cv)}
                              {q.unit ? ` ${q.unit}` : ""}
                            </div>
                            {showCompare && pv !== undefined && pv !== "" && pv !== null && (
                              <div className={`text-xs mt-0.5 ${diff ? "text-amber-700" : "text-muted-foreground"}`}>
                                <span className="opacity-70">Anterior:</span>{" "}
                                {Array.isArray(pv) ? pv.join(", ") : String(pv)}
                                {q.unit ? ` ${q.unit}` : ""}
                              </div>
                            )}
                          </dd>
                        </div>
                      );
                    })}
                  </dl>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminMetabolicTestsPage() {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [metabolic, catalog] = await Promise.all([
        api.get("/admin/metabolic-tests").catch(() => ({ data: [] })),
        api.get("/admin/test-submissions").catch(() => ({ data: [] })),
      ]);
      const metabolicRows = (metabolic.data || []).map((t) => ({
        ...t,
        test_kind: "metabolic",
        test_name: "Viva Melhor (metabólico)",
      }));
      const catalogRows = (catalog.data || []).map((t) => ({
        ...t,
        test_kind: "catalog",
      }));
      const merged = [...metabolicRows, ...catalogRows].sort((a, b) => {
        const ta = a.created_at || "";
        const tb = b.created_at || "";
        return tb.localeCompare(ta);
      });
      setTests(merged);
    } catch (_) {/* noop */}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const copyLink = async (token) => {
    const link = `${window.location.origin}/teste/${token}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link copiado!");
    } catch (_) { toast.error("Copie manualmente: " + link); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Excluir o teste de "${t.patient_name || "sem nome"}"?`)) return;
    try {
      if (t.test_kind === "catalog") {
        // Catalog submissions don't have a dedicated delete endpoint yet — no-op with feedback.
        toast.info("Submissões de testes do catálogo não podem ser excluídas por aqui ainda.");
        return;
      }
      await api.delete(`/admin/metabolic-tests/${t.id}`);
      toast.success("Teste excluído.");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao excluir."); }
  };

  const sendWhatsApp = (t) => {
    const digits = String(t.patient_whatsapp || "").replace(/\D+/g, "");
    if (!digits) {
      toast.error("Este teste não tem WhatsApp cadastrado.");
      return;
    }
    const firstName = (t.patient_name || "").trim().split(/\s+/)[0] || "";
    const reportPath = t.test_kind === "catalog"
      ? `/relatorio-teste/${t.token}`
      : `/relatorio-metabolico/${t.token}`;
    const link = `${window.location.origin}${reportPath}`;
    const msg =
      `Olá ${firstName}, sua análise de performance está pronta! ` +
      `Você pode conferir os detalhes e o seu score aqui: ${link}`;
    window.open(`https://wa.me/${digits}?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
  };

  const openView = (t) => {
    if (t.test_kind === "catalog") {
      // Reuse the public report as the "detailed answers" view for catalog subs.
      window.open(`/relatorio-teste/${t.token}`, "_blank", "noopener,noreferrer");
      return;
    }
    setViewing(t);
  };

  return (
    <div className="space-y-6" data-testid="admin-metabolic-tests-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-gold mb-3 inline-flex items-center gap-2"><FileText className="w-3.5 h-3.5" /> Biblioteca</div>
          <h1 className="brand-wordmark text-3xl md:text-4xl font-bold tracking-tight uppercase text-primary">Testes metabólicos</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            Envie um formulário público de pré-consulta ao paciente. Ele responde antes da visita e você recebe o contexto pronto.
          </p>
        </div>
        <button onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 rounded-sm bg-primary text-primary-foreground px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors"
          data-testid="new-metabolic-btn">
          <Plus className="w-4 h-4" /> Novo teste
        </button>
      </div>

      {loading ? (
        <div className="py-12 text-center text-sm text-muted-foreground overline">Carregando…</div>
      ) : tests.length === 0 ? (
        <div className="py-12 text-center text-sm text-muted-foreground">
          Nenhum teste criado ainda. Clique em <span className="font-semibold">&quot;Novo teste&quot;</span> para gerar seu primeiro link.
        </div>
      ) : (
        <div className="bg-card border border-border rounded-sm overflow-hidden">
          <div className="divide-y divide-border">
            {tests.map((t) => {
              const meta = STATUS_META[t.status] || STATUS_META.pending;
              const Icon = meta.icon;
              return (
                <div key={`${t.test_kind}-${t.id}`} className="p-4 hover:bg-muted/40 transition-colors" data-testid={`mt-row-${t.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center flex-wrap gap-2">
                        <div className="font-heading font-semibold">{t.patient_name || "Sem nome"}</div>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border text-[10px] font-semibold uppercase tracking-wider ${meta.cls}`}>
                          <Icon className="w-3 h-3" /> {meta.label}
                        </span>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border text-[10px] font-semibold uppercase tracking-wider ${
                          t.test_kind === "catalog"
                            ? "bg-primary/10 text-primary border-primary/30"
                            : "bg-gold/10 text-gold border-gold/30"
                        }`} data-testid={`mt-kind-${t.id}`}>
                          {t.test_name}
                        </span>
                      </div>
                      {t.patient_email && <div className="text-xs text-muted-foreground mt-1">{t.patient_email}</div>}
                      <div className="text-xs text-muted-foreground mt-1">
                        Criado {new Date(t.created_at).toLocaleString("pt-BR")}
                        {t.expires_at && <> · Expira {new Date(t.expires_at).toLocaleDateString("pt-BR")}</>}
                        {t.submitted_at && <> · <span className="text-emerald-700">Respondido {new Date(t.submitted_at).toLocaleString("pt-BR")}</span></>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {t.status === "pending" && t.test_kind !== "catalog" && (
                        <button onClick={() => copyLink(t.token)}
                          className="p-1.5 rounded-sm border border-border hover:bg-muted transition-colors inline-flex items-center gap-1 text-xs px-2"
                          data-testid={`mt-copy-${t.id}`}>
                          <Link2 className="w-3.5 h-3.5" /> Copiar link
                        </button>
                      )}
                      {t.status === "submitted" && (
                        <button onClick={() => openView(t)}
                          className="p-1.5 rounded-sm border border-primary text-primary hover:bg-primary/5 transition-colors inline-flex items-center gap-1 text-xs px-2"
                          data-testid={`mt-view-${t.id}`}>
                          {t.test_kind === "catalog"
                            ? <><ExternalLink className="w-3.5 h-3.5" /> Ver relatório</>
                            : <><Eye className="w-3.5 h-3.5" /> Ver respostas</>}
                        </button>
                      )}
                      {t.status === "submitted" && t.patient_whatsapp && (
                        <button onClick={() => sendWhatsApp(t)}
                          className="p-1.5 rounded-sm border border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700 transition-colors inline-flex items-center gap-1 text-xs px-2"
                          title={`Enviar relatório para ${t.patient_whatsapp}`}
                          data-testid={`mt-whatsapp-${t.id}`}>
                          <MessageCircle className="w-3.5 h-3.5" /> Enviar no WhatsApp
                        </button>
                      )}
                      <button onClick={() => remove(t)}
                        className="p-1.5 rounded-sm border border-destructive/30 text-destructive hover:bg-destructive/5 transition-colors"
                        data-testid={`mt-delete-${t.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {creating && <CreateModal onClose={() => setCreating(false)} onCreated={() => load()} />}
      {viewing && <ViewAnswersModal test={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

export { CreateModal as MetabolicCreateModal, ViewAnswersModal as MetabolicViewAnswersModal };
